import Papa from 'papaparse'
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  createImportPreview,
  updateImportPreview,
  setUnmatchedChains,
} from './importer-api'
import {
  CSVRow,
  ImportData,
  CSVRowState,
  ImportDataRowsFormat,
  InternalColumns,
} from './importer-types'
import { Product } from 'models/product'
import { TYPE_MAPPING } from 'components/UnifiedSearch/constants'
import { ChainSearchResult } from 'features/elastic/searchers'
import { useNavigate } from 'react-router-dom'
import useApiService from 'services/api'
import { toast } from 'react-toastify'
import { ColumnFilter, RowSelectionState } from '@tanstack/react-table'
import { ExportModalColumn } from 'components/Modals/ExportModal/ExportModal'
import { CompanyTypeSlug } from 'models/companies'
import { PATHS } from 'paths'
import { getColumnOption } from './columnMappingShared'

// === Phase 2 Helper Types and Functions ===
type CompanyInfo = {
  companyHash: string
  companyName: string
  isCustomCompany: boolean
  firstbiteId: string
  isImported: boolean
  companyTargetType: string | null
  /** Input fields from CSV mapped as company_match columns (label -> value) */
  inputFields: Record<string, string>
}

type HeaderLookups = {
  dealHeaders: { original: string; mapped: string }[]
  tagHeader: { original: string; mapped: string } | undefined
  gpoHeader: { original: string; mapped: string } | undefined
  contactHeaders: { original: string; mapped: string }[]
  notesHeader: { original: string; mapped: string } | undefined
  hasDealHeaders: boolean
  hasContactHeaders: boolean
}

type ImportCounts = {
  custom: { total: number; imported: number }
  tags: { total: number; imported: number }
  gpos: { total: number; imported: number }
  deals: { total: number; imported: number }
  contacts: { total: number; imported: number }
  notes: { total: number; imported: number }
}

function buildCompanyInfo(
  firstRow: CSVRow,
  companyHash: string,
  companyNameHeader: string | undefined,
  subcategoryIdToLabelMap: Record<number, string>,
  companyMatchHeaders: { original: string; mapped: string }[]
): CompanyInfo {
  const isCustomCompany = firstRow[InternalColumns.IS_CUSTOM_COMPANY]
  const setChainProxyId = firstRow[InternalColumns.SET_CHAIN_PROXY_ID]
  const matchedChain = firstRow[InternalColumns.MATCHED_CHAIN]

  let companyName: string
  if (isCustomCompany) {
    // Get the actual company name from the CSV when it's a custom/unmatched company
    companyName = companyNameHeader
      ? (firstRow[companyNameHeader] as string) || 'User-Generated'
      : 'User-Generated'
  } else if (matchedChain) {
    companyName = matchedChain.name || 'Unknown'
  } else {
    // Excluded company — use original CSV name with "(Excluded)" suffix
    const csvName = companyNameHeader
      ? (firstRow[companyNameHeader] as string)
      : null
    companyName = csvName ? `${csvName} (Excluded)` : 'Excluded'
  }

  let firstbiteId = ''
  if (isCustomCompany) {
    firstbiteId = 'UG' + setChainProxyId
  } else if (matchedChain) {
    firstbiteId = (matchedChain.type ?? '') + (matchedChain.item_id ?? '')
  }

  // Resolve company target type
  let companyTargetType: string | null = null
  if (isCustomCompany) {
    const subcatId = firstRow[InternalColumns.CUSTOM_COMPANY_SUB_CATEGORY_ID]
    if (subcatId != null) {
      companyTargetType = subcategoryIdToLabelMap[subcatId] ?? null
    }
  } else if (matchedChain?.type) {
    companyTargetType = TYPE_MAPPING[matchedChain.type] ?? null
  }

  // Extract all company_match input fields for custom companies
  const inputFields: Record<string, string> = {}
  if (isCustomCompany) {
    for (const header of companyMatchHeaders) {
      const value = firstRow[header.original]
      if (value != null && value !== '') {
        const label = getColumnOption(header.mapped)?.name || header.mapped
        inputFields[label] = String(value)
      }
    }
  }

  return {
    companyHash,
    companyName,
    isCustomCompany,
    firstbiteId,
    isImported: setChainProxyId !== -1,
    companyTargetType,
    inputFields,
  }
}

function processDealRow(
  row: CSVRow,
  headers: HeaderLookups,
  companyInfo: CompanyInfo,
  summaryDeals: Record<string, unknown>[],
  detailedDeals: Record<string, unknown>[],
  counts: ImportCounts,
  isSet: boolean
): void {
  if (!headers.hasDealHeaders) return

  const dealData: Record<string, unknown> = {}

  headers.dealHeaders.forEach((header) => {
    dealData[header.mapped] = row[header.original] ?? ''
  })

  if (row[InternalColumns.DEAL_PRODUCT]) {
    dealData['deal_product'] = row[InternalColumns.DEAL_PRODUCT]
  }

  const isImported = row[InternalColumns.DEAL_SET] !== -1
  summaryDeals.push(dealData)
  detailedDeals.push({ ...companyInfo, ...dealData, isImported })
  if (isSet) {
    counts.deals.total++
    if (isImported) counts.deals.imported++
  }
}

function processTagRow(
  row: CSVRow,
  headers: HeaderLookups,
  companyInfo: CompanyInfo,
  summaryTagsSet: Set<string>,
  detailedTags: Record<string, unknown>[],
  counts: ImportCounts,
  isSet: boolean
): void {
  if (!headers.tagHeader) return

  const tagValue = row[headers.tagHeader.original]
  if (!tagValue) return

  const isImported = row[InternalColumns.TAG_SET] !== -1
  const tagList = String(tagValue)
    .split(',')
    .map((t) => t.trim())
    .filter((t) => t.length > 0)

  tagList.forEach((tag) => {
    summaryTagsSet.add(tag)
    detailedTags.push({ ...companyInfo, tag_name: tag, isImported })
    if (isSet) {
      counts.tags.total++
      if (isImported) counts.tags.imported++
    }
  })
}

function processGpoRow(
  row: CSVRow,
  headers: HeaderLookups,
  companyInfo: CompanyInfo,
  summaryGposSet: Set<string>,
  detailedGpos: Record<string, unknown>[],
  counts: ImportCounts,
  isSet: boolean
): void {
  if (!headers.gpoHeader) return

  const gpoValue = row[headers.gpoHeader.original]
  if (!gpoValue) return

  const isImported = row[InternalColumns.GPO_SET] !== -1
  const gpoList = String(gpoValue)
    .split(',')
    .map((g) => g.trim())
    .filter((g) => g.length > 0)

  gpoList.forEach((gpo) => {
    summaryGposSet.add(gpo)
    detailedGpos.push({ ...companyInfo, gpo_name: gpo, isImported })
    if (isSet) {
      counts.gpos.total++
      if (isImported) counts.gpos.imported++
    }
  })
}

function processContactRow(
  row: CSVRow,
  headers: HeaderLookups,
  companyInfo: CompanyInfo,
  summaryContacts: Record<string, unknown>[],
  detailedContacts: Record<string, unknown>[],
  counts: ImportCounts,
  isSet: boolean,
  seenContactHashes: Set<number>,
  importedContactHashes: Set<number>
): void {
  if (!headers.hasContactHeaders) return

  const contactData: Record<string, unknown> = {}
  let hasData = false

  headers.contactHeaders.forEach((header) => {
    const value = row[header.original]
    if (value !== undefined && value !== null && value !== '') {
      contactData[header.mapped] = value
      hasData = true
    }
  })

  if (hasData) {
    const isImported = row[InternalColumns.CONTACT_SET] !== -1
    const failed_import_reason =
      row[InternalColumns.FAILED_IMPORT_REASON] || null
    const contactHash = row[InternalColumns.CONTACT_HASH]

    // Only count and show unique contacts based on hash
    if (isSet && contactHash !== undefined) {
      // Count total unique contacts (by hash)
      if (!seenContactHashes.has(contactHash)) {
        seenContactHashes.add(contactHash)
        counts.contacts.total++
        summaryContacts.push(contactData)
        // Add to detailed list only for unique contacts
        detailedContacts.push({
          ...companyInfo,
          ...contactData,
          isImported,
          failed_import_reason,
        })
      }
      // Count unique imported contacts (by hash)
      if (isImported && !importedContactHashes.has(contactHash)) {
        importedContactHashes.add(contactHash)
        counts.contacts.imported++
      }
    } else if (isSet) {
      // Fallback if no hash (shouldn't happen, but safe)
      summaryContacts.push(contactData)
      detailedContacts.push({
        ...companyInfo,
        ...contactData,
        isImported,
        failed_import_reason,
      })
      counts.contacts.total++
      if (isImported) counts.contacts.imported++
    }
  }
}

function processNoteRow(
  row: CSVRow,
  headers: HeaderLookups,
  companyInfo: CompanyInfo,
  summaryNotesSet: Set<string>,
  detailedNotes: Record<string, unknown>[],
  counts: ImportCounts,
  isSet: boolean
): void {
  if (!headers.notesHeader) return

  const noteValue = row[headers.notesHeader.original]
  if (!noteValue) return

  const isImported = row[InternalColumns.COMPANY_NOTES_SET] !== -1
  const note = String(noteValue).trim()
  if (!note) return

  summaryNotesSet.add(note)
  detailedNotes.push({ ...companyInfo, company_note: note, isImported })
  if (isSet) {
    counts.notes.total++
    if (isImported) counts.notes.imported++
  }
}
// === End Phase 2 Helper Functions ===

// === Context Setup ===

type ExportConfig = {
  allowSuggestions: boolean
  suggestionAmount: number
}

type DetailedImportData = {
  deals: Record<string, unknown>[]
  tags: Record<string, unknown>[]
  gpos: Record<string, unknown>[]
  contacts: Record<string, unknown>[]
  notes: Record<string, unknown>[]
  custom: CompanyInfo[]
}

export interface ImporterContextType {
  // Data
  rawFile: File | null
  importData: ImportData | null
  products: Product[] | undefined

  // API data for validation modal
  users: { email: string }[] | undefined
  salesStages: { stage: string }[] | undefined

  // Modal states
  showHeaderMappingModal: boolean
  setShowHeaderMappingModal: Dispatch<SetStateAction<boolean>>
  showSelectDealProductModal: boolean
  selectedDealProduct: string | null
  setSelectedDealProduct: Dispatch<SetStateAction<string | null>>
  showConfigureExportModal: boolean
  setShowConfigureExportModal: Dispatch<SetStateAction<boolean>>

  // Skip matching
  skipMatching: boolean
  setSkipMatching: Dispatch<SetStateAction<boolean>>

  // Loading states
  isSaving: boolean
  isUploading: boolean
  isSettingUnmatchedChains: boolean

  // File upload
  fileUploadError: string | null

  // Form states
  hasPendingChanges: boolean
  selectedTab: CSVRowState
  setSelectedTab: Dispatch<SetStateAction<CSVRowState>>
  rowSelection: RowSelectionState
  setRowSelection: Dispatch<SetStateAction<RowSelectionState>>
  setShowSelectDealProductModal: Dispatch<SetStateAction<boolean>>
  showExportModal: boolean
  setShowExportModal: Dispatch<SetStateAction<boolean>>

  // Computed values
  companySubcategories:
    | {
        category_id: number
        value: number
        label: unknown
        slug: CompanyTypeSlug
        category_name: string
        subcategory_name: string
      }[]
    | undefined
  mappedColumns: string[] | undefined
  tableData: Record<CSVRowState, CSVRow[]>
  wizardHeaders: Record<string, string>
  otherOtherType:
    | { category_id: number; value: number; slug: CompanyTypeSlug }
    | undefined
  areAllRowsSet: boolean

  // From computedCompanyData
  companyGroups: Map<string, CSVRow[]>
  companiesPrematched: CSVRow[]
  companiesPendingReview: CSVRow[]
  companiesLeftUnmatched: CSVRow[]
  allCompanies: CSVRow[]
  amountCompaniesPrematched: number
  amountCompaniesPendingReview: number
  amountCompaniesLeftUnmatched: number
  importSummaryData: Record<string, unknown>[]
  detailedImportData: DetailedImportData
  importCounts: ImportCounts
  isAllImported: boolean
  uniqueSubcategoryIds: Set<number>

  // Data mutation
  updateImportData: (
    transform: (data: ImportData) => ImportData
  ) => ImportData | null

  // Handlers
  handleSelectDealProduct: (product: string) => void
  handleExport: (selectedColumns: string[]) => Promise<void>
  handleFileUpload: (file: File | null) => void
  clearFileState: () => void
  handleSetUnmatchedChains: () => Promise<void>
  handleSave: (
    data?: ImportData,
    skipMatching?: boolean
  ) => Promise<number | undefined>
  shouldShowBulkActions: boolean
  selectedRowIds: string[]
  chainMergerAvailableSubcategories: Record<number, string>
  handleConfigureExport: () => void

  // Column Filtering
  columnFiltering: ColumnFilter[]
  setSubcategoryFilter: (subcategoryId: number) => void
  clearSubcategoryFilter: () => void
  selectedSubcategoryFilter: unknown

  // Export
  exportColumns: ExportModalColumn[]
  exportConfig: ExportConfig
  setExportConfig: Dispatch<SetStateAction<ExportConfig>>

  // Review
  reviewing: boolean
  setReviewing: Dispatch<SetStateAction<boolean>>
  handleReview: () => void
}

const ImporterContext = createContext<ImporterContextType | null>(null)

export function ImporterProvider({
  importNumber,
  initialImportData,
  initialSkipMatching = false,
  dataVersion,
  children,
}: {
  importNumber: number | null
  /** Import data from the wizard machine - used as initial/updated value */
  initialImportData: ImportData | null
  /** Skip matching mode from wizard machine */
  initialSkipMatching?: boolean
  /** Data version from wizard machine - triggers reset when data is refreshed */
  dataVersion: number
  children: ReactNode
}) {
  const navigate = useNavigate()
  const api = useApiService()

  const { data: companyCategories } = api.useGetCompanyCategories()
  const { data: salesStages } = api.useGetSalesStages()
  const { data: users } = api.useGetUsers()
  const { data: products } = api.useGetProducts()

  const [showSelectDealProductModal, setShowSelectDealProductModal] =
    useState(false)
  const [selectedDealProduct, setSelectedDealProduct] = useState<string | null>(
    null
  )
  const [isSaving, setIsSaving] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [isSettingUnmatchedChains, setIsSettingUnmatchedChains] =
    useState(false)
  // Import data - initialized from machine, updated locally for edits
  const [importData, setImportData] = useState<ImportData | null>(
    initialImportData
  )
  const [rawFile, setRawFile] = useState<File | null>(null)
  const [fileUploadError, setFileUploadError] = useState<string | null>(null)
  const [hasPendingChanges, setHasPendingChanges] = useState(false)
  const [selectedTab, setSelectedTab] = useState<CSVRowState>('pending_review')
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})

  const [columnFiltering, setColumnFiltering] = useState<ColumnFilter[]>([])
  const [showHeaderMappingModal, setShowHeaderMappingModal] = useState(false)
  const [showExportModal, setShowExportModal] = useState(false)
  const [showConfigureExportModal, setShowConfigureExportModal] =
    useState(false)
  // Skip matching - initialized from machine
  const [skipMatching, setSkipMatching] = useState(initialSkipMatching)
  const [reviewing, setReviewing] = useState(true)

  const [exportConfig, setExportConfig] = useState<ExportConfig>({
    allowSuggestions: true,
    suggestionAmount: 1,
  })

  // Sync importData when machine refreshes data (dataVersion changes)
  useEffect(() => {
    if (initialImportData) {
      setImportData(initialImportData)
      setHasPendingChanges(false)
    }
  }, [dataVersion, initialImportData])

  // Sync skipMatching when machine refreshes
  useEffect(() => {
    setSkipMatching(initialSkipMatching)
  }, [dataVersion, initialSkipMatching])

  /* -------------
  Computed Values
  ------------- */

  const exportColumns = useMemo<ExportModalColumn[]>(() => {
    if (!importData) return []

    function toTitle(key: string) {
      return key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase())
    }

    const baseColumns: ExportModalColumn['elements'] = []
    importData.headers.forEach((h) => {
      baseColumns.push([h.mapped, h.original])
    })

    const suggestionColumns: ExportModalColumn['elements'] = []
    chainSuggestionKeys.forEach((key) => {
      suggestionColumns.push([
        `__suggestion__chain__${key}`,
        `Chain ${toTitle(key)}`,
      ])
    })

    const generatedColumns: ExportModalColumn['elements'] = []
    generatedColumnKeys.forEach((key) => {
      generatedColumns.push([
        key,
        `${toTitle(key.replace('__generated__', ''))}`,
      ])
    })

    const groups = [
      {
        title: 'Uploaded Data',
        elements: baseColumns,
      },
      {
        title: 'Matched Data',
        elements: generatedColumns,
      },
    ]

    if (exportConfig.allowSuggestions) {
      groups.push({
        title: 'Suggested Data',
        elements: suggestionColumns,
      })
    }

    return groups
  }, [importData, exportConfig])

  const companySubcategories = useMemo(() => {
    return companyCategories?.reduce(
      (acc, c) => {
        c.sub_categories?.forEach((s) => {
          acc.push({
            label: (
              <p className="text-sm">
                {c.name} - {s.name}
              </p>
            ),
            value: s.id,
            category_id: c.id,
            slug: s.slug,
            category_name: c.name,
            subcategory_name: s.name,
          })
        })
        return acc
      },
      [] as {
        label: any
        value: number
        category_id: number
        slug: CompanyTypeSlug
        category_name: string
        subcategory_name: string
      }[]
    )
  }, [companyCategories])

  // Map subcategory IDs to human-readable labels for CompanyInfo
  const subcategoryIdToLabelMap = useMemo(() => {
    const map: Record<number, string> = {}
    companySubcategories?.forEach((s) => {
      map[s.value] = `${s.category_name} / ${s.subcategory_name}`
    })
    return map
  }, [companySubcategories])

  const mappedColumns = useMemo(() => {
    return importData?.headers.map((h) => h.mapped)
  }, [importData])

  const shouldShowBulkActions = useMemo(() => {
    return Object.keys(rowSelection).filter((r) => rowSelection[r]).length > 0
  }, [rowSelection])

  const selectedRowIds = useMemo(() => {
    return Object.keys(rowSelection).filter((r) => rowSelection[r])
  }, [rowSelection])

  const selectedSubcategoryFilter = useMemo(() => {
    return columnFiltering.find(
      (f) => f.id === InternalColumns.EXISTING_CHAIN_PROXY_SUBCATEGORY_ID
    )?.value
  }, [columnFiltering])

  const wizardHeaders = useMemo(() => {
    const map = {
      name: '',
      domain: '',
      address: '',
      street: '',
      city: '',
      state: '',
      zip: '',
      full_address: '',
    }

    importData?.headers.forEach((header) => {
      switch (header.mapped) {
        case 'company_name':
          map.name = header.original
          break
        case 'company_domain':
          map.domain = header.original
          break
        case 'company_address_street':
          map.street = header.original
          break
        case 'company_address_city':
          map.city = header.original
          break
        case 'company_address_state':
          map.state = header.original
          break
        case 'company_address_zip':
          map.zip = header.original
          break
        case 'company_full_address':
          map.full_address = header.original
          break
      }
    })

    return map
  }, [importData?.headers])

  const otherOtherType = useMemo(() => {
    return companySubcategories?.find((subcat) => subcat.slug === 'other-other')
  }, [companySubcategories])

  const computedCompanyData = useMemo(() => {
    if (!importData) {
      return {
        // Phase 1 values
        companyGroups: new Map<string, CSVRow[]>(),
        companiesPrematched: [] as CSVRow[],
        companiesPendingReview: [] as CSVRow[],
        companiesLeftUnmatched: [] as CSVRow[],
        allCompanies: [] as CSVRow[],
        amountCompaniesPrematched: 0,
        amountCompaniesPendingReview: 0,
        amountCompaniesLeftUnmatched: 0,
        // Phase 2 values
        importSummaryData: [] as Record<string, unknown>[],
        detailedImportData: {
          deals: [] as Record<string, unknown>[],
          tags: [] as Record<string, unknown>[],
          gpos: [] as Record<string, unknown>[],
          contacts: [] as Record<string, unknown>[],
          notes: [] as Record<string, unknown>[],
          custom: [] as CompanyInfo[],
        },
        importCounts: {
          custom: { total: 0, imported: 0 },
          tags: { total: 0, imported: 0 },
          gpos: { total: 0, imported: 0 },
          deals: { total: 0, imported: 0 },
          contacts: { total: 0, imported: 0 },
          notes: { total: 0, imported: 0 },
        },
        isAllImported: true,
        areAllRowsSet: false,
        tableData: { pending_review: [] as CSVRow[], reviewed: [] as CSVRow[] },
        uniqueSubcategoryIds: new Set<number>(),
      }
    }

    // --- Header lookups (once, before loop) ---
    const companyNameHeader = importData.headers.find(
      (h) => h.mapped === 'company_name'
    )?.original

    const companyMatchHeaders = importData.headers.filter(
      (h) => getColumnOption(h.mapped)?.type === 'company_match'
    )

    const headers: HeaderLookups = {
      dealHeaders: importData.headers.filter(
        (h) => getColumnOption(h.mapped)?.type === 'deal_data'
      ),
      tagHeader: importData.headers.find(
        (h) => getColumnOption(h.mapped)?.type === 'tag_data'
      ),
      gpoHeader: importData.headers.find(
        (h) => getColumnOption(h.mapped)?.type === 'gpo_data'
      ),
      contactHeaders: importData.headers.filter(
        (h) => getColumnOption(h.mapped)?.type === 'contact_data'
      ),
      notesHeader: importData.headers.find(
        (h) => getColumnOption(h.mapped)?.type === 'company_notes'
      ),
      hasDealHeaders: false,
      hasContactHeaders: false,
    }
    headers.hasDealHeaders = headers.dealHeaders.length > 0
    headers.hasContactHeaders = headers.contactHeaders.length > 0

    const companyGroups = new Map<string, CSVRow[]>()
    const allCompanies: CSVRow[] = []
    const companiesPrematched: CSVRow[] = []
    const companiesPendingReview: CSVRow[] = []
    const companiesLeftUnmatched: CSVRow[] = []

    const deals: Record<string, unknown>[] = []
    const tags: Record<string, unknown>[] = []
    const gpos: Record<string, unknown>[] = []
    const contacts: Record<string, unknown>[] = []
    const notes: Record<string, unknown>[] = []
    const custom: CompanyInfo[] = []

    const counts: ImportCounts = {
      custom: { total: 0, imported: 0 },
      tags: { total: 0, imported: 0 },
      gpos: { total: 0, imported: 0 },
      deals: { total: 0, imported: 0 },
      contacts: { total: 0, imported: 0 },
      notes: { total: 0, imported: 0 },
    }

    // Row-level counters for areAllRowsSet
    let readyRowCount = 0
    let setRowCount = 0

    // Row arrays for tableData
    const reviewedRows: CSVRow[] = []
    const pendingReviewRows: CSVRow[] = []

    // Unique subcategory IDs for chainMergerAvailableSubcategories
    const uniqueSubcategoryIds = new Set<number>()

    // Contact hash tracking for deduplication
    const seenContactHashes = new Set<number>()
    const importedContactHashes = new Set<number>()

    const companySummaryMap = new Map<
      string,
      {
        companyInfo: CompanyInfo
        summaryDeals: Record<string, unknown>[]
        summaryTagsSet: Set<string>
        summaryGposSet: Set<string>
        summaryNotesSet: Set<string>
        summaryContacts: Record<string, unknown>[]
      }
    >()

    // === SINGLE LOOP OVER ALL ROWS ===
    for (const row of importData.rows) {
      const hash = row[InternalColumns.COMPANY_HASH]
      const isReady = row[InternalColumns.READY]
      const hasMatch =
        row[InternalColumns.MATCHED_CHAIN] || row[InternalColumns.MATCHED_DOOR]
      const isSet =
        row[InternalColumns.SET_CHAIN_PROXY_ID] !== -1 ||
        row[InternalColumns.IS_CHAIN_MERGED]

      // Count rows for areAllRowsSet
      if (isReady) readyRowCount++
      if (isSet) setRowCount++

      // Collect rows for tableData
      if (isSet) {
        reviewedRows.push(row)
      } else {
        pendingReviewRows.push(row)
      }

      // Collect unique subcategory IDs
      const subcatId = row[InternalColumns.EXISTING_CHAIN_PROXY_SUBCATEGORY_ID]
      if (subcatId !== undefined && subcatId !== null) {
        uniqueSubcategoryIds.add(subcatId)
      }

      // First time seeing this company?
      if (!companyGroups.has(hash)) {
        companyGroups.set(hash, [])
        allCompanies.push(row)

        // Phase 1: Categorize company
        if (isReady) {
          companiesPrematched.push(row)
        } else {
          companiesPendingReview.push(row)
          if (!hasMatch) {
            companiesLeftUnmatched.push(row)
          }
        }

        // Phase 2: Initialize company accumulators
        const companyInfo = buildCompanyInfo(
          row,
          hash,
          companyNameHeader,
          subcategoryIdToLabelMap,
          companyMatchHeaders
        )
        companySummaryMap.set(hash, {
          companyInfo,
          summaryDeals: [],
          summaryTagsSet: new Set<string>(),
          summaryGposSet: new Set<string>(),
          summaryNotesSet: new Set<string>(),
          summaryContacts: [],
        })

        // Custom companies (once per company)
        if (companyInfo.isCustomCompany) {
          custom.push(companyInfo)
          counts.custom.total++
          if (companyInfo.isImported) counts.custom.imported++
        }
      }

      companyGroups.get(hash)!.push(row)

      // Phase 2: Process row data (helper functions update counts inline)
      const summary = companySummaryMap.get(hash)!
      processDealRow(
        row,
        headers,
        summary.companyInfo,
        summary.summaryDeals,
        deals,
        counts,
        isSet
      )
      processTagRow(
        row,
        headers,
        summary.companyInfo,
        summary.summaryTagsSet,
        tags,
        counts,
        isSet
      )
      processGpoRow(
        row,
        headers,
        summary.companyInfo,
        summary.summaryGposSet,
        gpos,
        counts,
        isSet
      )
      processContactRow(
        row,
        headers,
        summary.companyInfo,
        summary.summaryContacts,
        contacts,
        counts,
        isSet,
        seenContactHashes,
        importedContactHashes
      )
      processNoteRow(
        row,
        headers,
        summary.companyInfo,
        summary.summaryNotesSet,
        notes,
        counts,
        isSet
      )
    }

    const importSummaryData = Array.from(companySummaryMap.values()).map(
      ({
        companyInfo,
        summaryDeals,
        summaryTagsSet,
        summaryGposSet,
        summaryNotesSet,
        summaryContacts,
      }) => ({
        ...companyInfo,
        deals: summaryDeals,
        tags: Array.from(summaryTagsSet),
        gpos: Array.from(summaryGposSet),
        notes: Array.from(summaryNotesSet),
        contacts: summaryContacts,
      })
    )

    const hasData =
      counts.custom.total > 0 ||
      counts.tags.total > 0 ||
      counts.gpos.total > 0 ||
      counts.deals.total > 0 ||
      counts.contacts.total > 0 ||
      counts.notes.total > 0

    const isAllImported =
      !hasData ||
      (counts.custom.imported === counts.custom.total &&
        counts.tags.imported === counts.tags.total &&
        counts.gpos.imported === counts.gpos.total &&
        counts.deals.imported === counts.deals.total &&
        counts.contacts.imported === counts.contacts.total &&
        counts.notes.imported === counts.notes.total)

    const areAllRowsSet = setRowCount === readyRowCount && setRowCount > 0

    return {
      companyGroups,
      companiesPrematched,
      companiesPendingReview,
      companiesLeftUnmatched,
      allCompanies,
      amountCompaniesPrematched: companiesPrematched.length,
      amountCompaniesPendingReview: companiesPendingReview.length,
      amountCompaniesLeftUnmatched: companiesLeftUnmatched.length,
      importSummaryData,
      detailedImportData: { deals, tags, gpos, contacts, notes, custom },
      importCounts: counts,
      isAllImported,
      areAllRowsSet,
      tableData: { pending_review: pendingReviewRows, reviewed: reviewedRows },
      uniqueSubcategoryIds,
    }
  }, [importData, subcategoryIdToLabelMap])

  const chainMergerAvailableSubcategories = useMemo<
    Record<number, string>
  >(() => {
    const subCats =
      companySubcategories?.filter((s) =>
        computedCompanyData.uniqueSubcategoryIds.has(s.value)
      ) ?? []
    const subCatMap: Record<number, string> = {}
    for (const s of subCats) {
      subCatMap[s.value] = s.label
    }
    return subCatMap
  }, [computedCompanyData.uniqueSubcategoryIds, companySubcategories])

  /* -------------
  Field Handlers
  ------------- */

  /**
   * Apply a pure transform to importData and update state.
   * Returns the new ImportData so callers can use it immediately
   * (without waiting for React to re-render).
   */
  function updateImportData(
    transform: (data: ImportData) => ImportData
  ): ImportData | null {
    if (!importData) return null
    const newData = transform(importData)
    setImportData(newData)
    setHasPendingChanges(true)
    return newData
  }

  /* -------------
  Actions
  ------------- */
  async function handleSave(
    localImportData?: ImportData,
    localSkipMatching?: boolean
  ) {
    setIsSaving(true)

    let dataToSave: ImportData | null = null
    if (localImportData) {
      dataToSave = { ...localImportData }
    } else if (importData) {
      dataToSave = { ...importData }
    }

    if (!dataToSave) {
      return
    }

    const data = toRowsJSON(dataToSave)
    const skipMatchingValue =
      localSkipMatching !== undefined ? localSkipMatching : skipMatching

    try {
      // Update (skip_matching is only set on creation, never updated)
      if (importNumber) {
        await updateImportPreview(importNumber, {
          import_data: { data },
        })
      } else {
        // Create
        const rawDataString = await rawFile?.text()
        const fileName = rawFile?.name
        const response = await createImportPreview({
          import_data: {
            data,
            raw_data: rawDataString ?? '',
            name: fileName ?? '',
            skip_matching: skipMatchingValue,
          },
        })
        navigate(`${PATHS.IMPORT_WIZARD}${response.import_number}`, {
          replace: true,
        })
        return response.import_number
      }
      setHasPendingChanges(false)
      return importNumber
    } catch {
      toast.error('Error saving import preview')
    } finally {
      setIsSaving(false)
    }
  }

  async function handleReview() {
    setReviewing(true)
  }

  function handleSelectDealProduct(productName: string) {
    if (!importData) return

    setImportData({
      ...importData,
      rows: importData.rows.map((row) => ({
        ...row,
        [InternalColumns.DEAL_PRODUCT]: productName,
      })),
      headers: [
        ...importData.headers,
        {
          original: InternalColumns.DEAL_PRODUCT,
          mapped: 'deal_product',
          label: 'Deal Product',
        },
      ],
    })

    setShowSelectDealProductModal(false)
  }

  function handleConfigureExport() {
    setShowConfigureExportModal(false)
    setShowExportModal(true)
  }

  const handleExport = useCallback(
    async (selectedColumns: string[]) => {
      if (!importData) return

      const data = importData.rows

      // Transform rows to include exploded matched fields
      const transformedRows: Record<string, any>[] = []
      for (const row of data) {
        const baseRow: Record<string, any> = {}

        function addColumn(key: string, value: any, row: Record<string, any>) {
          if (selectedColumns.includes(key)) {
            row[key] = value
          }
        }

        // Remove nested objects
        for (const key in row) {
          if (
            [
              InternalColumns.ROW_ID,
              InternalColumns.EXISTING_CHAIN_PROXY_ID,
              InternalColumns.EXISTING_CHAIN_PROXY_TYPE,
              InternalColumns.EXISTING_CHAIN_PROXY_SUBCATEGORY_ID,
              InternalColumns.SET_CHAIN_PROXY_ID,
              InternalColumns.MATCHED_DOOR,
              InternalColumns.MATCHED_CHAIN,
              InternalColumns.SUGGESTED_DOORS,
              InternalColumns.SUGGESTED_CHAINS,
              InternalColumns.IS_CUSTOM_COMPANY,
              InternalColumns.CUSTOM_COMPANY_CATEGORY_ID,
              InternalColumns.CUSTOM_COMPANY_SUB_CATEGORY_ID,
              InternalColumns.READY,
              InternalColumns.IS_CHAIN_MERGED,
              InternalColumns.IS_DOOR_MANUAL_MATCH,
              InternalColumns.IS_CHAIN_MANUAL_MATCH,
              InternalColumns.TAG_SET,
              InternalColumns.GPO_SET,
              InternalColumns.DEAL_SET,
              InternalColumns.CONTACT_SET,
              InternalColumns.COMPANY_NOTES_SET,
              InternalColumns.DEAL_PRODUCT,
              InternalColumns.MATCHED_CHAIN_PK,
              InternalColumns.MATCHED_CHAIN_TYPE,
              InternalColumns.COMPANY_HASH,
              InternalColumns.CONTACT_HASH,
            ].includes(key as InternalColumns) ||
            key.startsWith('matching__')
          ) {
            continue
          }
          baseRow[key] = row[key]
        }

        // ### SUGGESTIONS COLUMNS ###

        // Add blank columns for completeness
        chainSuggestionKeys.forEach((key) => {
          addColumn(`__suggestion__chain__${key}`, '', baseRow)
        })

        if (
          // Show only if row is not set - i.e. not imported
          row[InternalColumns.SET_CHAIN_PROXY_ID] === -1 &&
          exportConfig.allowSuggestions
        ) {
          // Add the first suggestions to base row
          chainSuggestionKeys.forEach((key) => {
            addColumn(
              `__suggestion__chain__${key}`,
              row[InternalColumns.SUGGESTED_CHAINS]?.[0]?.[key] ?? '',
              baseRow
            )
          })

          // Add extra suggestions as new rows
          const extraSuggestionRows = []
          if (
            row[InternalColumns.SUGGESTED_CHAINS]?.length &&
            row[InternalColumns.SUGGESTED_CHAINS].length > 1
          ) {
            for (const s of row[InternalColumns.SUGGESTED_CHAINS].slice(
              1,
              exportConfig.suggestionAmount
            )) {
              const rowWithSuggestions = { ...baseRow }
              chainSuggestionKeys.forEach((key) => {
                addColumn(
                  `__suggestion__chain__${key}`,
                  s[key],
                  rowWithSuggestions
                )
              })
              extraSuggestionRows.push(rowWithSuggestions)
            }
          }
          if (extraSuggestionRows.length > 0) {
            transformedRows.push(...extraSuggestionRows)
          }
        }

        // ### MATCHED COLUMNS ###
        // Add blank columns
        addColumn('__generated__firstbite_chain_id', '', baseRow)
        addColumn(
          '__generated__chain_name',
          row[InternalColumns.MATCHED_CHAIN]?.name ?? '',
          baseRow
        )
        addColumn(
          '__generated__chain_domain',
          row[InternalColumns.MATCHED_CHAIN]?.domain ?? '',
          baseRow
        )
        addColumn(
          '__generated__chain_size',
          row[InternalColumns.MATCHED_CHAIN]?.count ?? '',
          baseRow
        )

        // Row is set - Add FB ID and Door ID
        if (row[InternalColumns.SET_CHAIN_PROXY_ID] !== -1) {
          // FB ID
          // Get Prefixed Firstbite ID e.g. RE12312
          let firstbiteId =
            (row[InternalColumns.MATCHED_CHAIN]?.type ?? '') +
            (row[InternalColumns.MATCHED_CHAIN]?.item_id ?? '')
          if (row[InternalColumns.IS_CUSTOM_COMPANY]) {
            firstbiteId = 'UG' + row[InternalColumns.SET_CHAIN_PROXY_ID]
          }
          addColumn('__generated__firstbite_chain_id', firstbiteId, baseRow)
        }

        transformedRows.push(baseRow)
      }

      // Convert to CSV
      const csv = Papa.unparse(transformedRows)

      // Create and trigger download
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', 'exported_data.csv')
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    [importData, exportConfig]
  )

  function handleFileUpload(file: File | null) {
    setFileUploadError(null)
    setRawFile(file)
    if (!file) return

    if (!file.name.toLowerCase().endsWith('.csv')) {
      setFileUploadError(
        'Only CSV files are supported. Please upload a .csv file.'
      )
      setRawFile(null)
      return
    }

    setIsUploading(true)
    Papa.parse<CSVRow>(file, {
      header: true,
      skipEmptyLines: 'greedy',
      delimitersToGuess: [';', ','],
      complete: (results) => {
        const columns = results.meta.fields ?? []
        if (columns.length < 2) {
          setFileUploadError(
            'The file could not be parsed as a valid CSV. Ensure the file uses comma or semicolon delimiters and has at least 2 columns.'
          )
          setRawFile(null)
          setIsUploading(false)
          return
        }

        if (results.data.length === 0) {
          setFileUploadError('The CSV file has no data rows.')
          setRawFile(null)
          setIsUploading(false)
          return
        }

        const data = results.data.map((row) => {
          return {
            ...Object.fromEntries(
              Object.entries(row).map(([key, value]) => [
                key,
                typeof value === 'string' ? value : undefined,
              ])
            ),
          }
        }) as CSVRow[]
        const importData = {
          rows: data,
          headers:
            columns.map((h) => ({
              original: h,
              mapped: 'ignore',
              label: undefined,
            })) ?? [],
        }
        setImportData(importData)
        setShowHeaderMappingModal(true)
        setIsUploading(false)
      },
      error: (error) => {
        console.error('Error parsing CSV:', error)
        setFileUploadError(
          'Failed to parse the CSV file. Please check the file format.'
        )
        setRawFile(null)
        setIsUploading(false)
      },
    })
  }

  function clearFileState() {
    setRawFile(null)
    setImportData(null)
    setFileUploadError(null)
  }

  async function handleSetUnmatchedChains() {
    if (!importData) return
    if (!importNumber) return
    try {
      setIsSettingUnmatchedChains(true)
      await handleSave()
      await setUnmatchedChains(importNumber)
      // Machine will detect process completion and refresh data
    } catch (error) {
      console.error(error)
    } finally {
      setIsSettingUnmatchedChains(false)
    }
  }

  function setSubcategoryFilter(subcategoryId: number) {
    if (Number.isNaN(subcategoryId)) {
      clearSubcategoryFilter()
      return
    }

    setColumnFiltering([
      {
        id: InternalColumns.EXISTING_CHAIN_PROXY_SUBCATEGORY_ID,
        value: subcategoryId,
      },
    ])
  }

  function clearSubcategoryFilter() {
    setColumnFiltering([])
  }

  const value = {
    // Data
    rawFile,
    importData,
    products,

    // API data for validation modal
    users,
    salesStages,

    // Modal states
    showHeaderMappingModal,
    setShowHeaderMappingModal,
    showSelectDealProductModal,
    selectedDealProduct,
    setSelectedDealProduct,
    showConfigureExportModal,
    setShowConfigureExportModal,

    // Skip matching
    skipMatching,
    setSkipMatching,

    // Loading states
    isSaving,
    isUploading,
    isSettingUnmatchedChains,

    // File upload
    fileUploadError,

    // Form states
    hasPendingChanges,
    selectedTab,
    setSelectedTab,
    rowSelection,
    setRowSelection,
    setShowSelectDealProductModal,
    showExportModal,
    setShowExportModal,

    // Computed values
    companySubcategories,
    mappedColumns,
    wizardHeaders,
    otherOtherType,
    // From computedCompanyData (Phase 1+2 consolidation)
    // Includes: companyGroups, companiesPrematched, companiesPendingReview,
    // companiesLeftUnmatched, allCompanies, amountCompaniesPrematched,
    // amountCompaniesPendingReview, amountCompaniesLeftUnmatched,
    // importSummaryData, detailedImportData, importCounts, isAllImported,
    // areAllRowsSet, tableData
    ...computedCompanyData,

    // Data mutation
    updateImportData,

    // Handlers
    handleSelectDealProduct,
    handleExport,
    handleFileUpload,
    clearFileState,
    handleSetUnmatchedChains,
    handleSave,
    shouldShowBulkActions,
    selectedRowIds,
    chainMergerAvailableSubcategories,
    handleConfigureExport,

    // Column Filtering
    columnFiltering,
    setSubcategoryFilter,
    clearSubcategoryFilter,
    selectedSubcategoryFilter,

    // Export
    exportColumns,
    exportConfig,
    setExportConfig,

    // Review
    reviewing,
    setReviewing,
    handleReview,
  }

  return (
    <ImporterContext.Provider value={value}>
      {children}
    </ImporterContext.Provider>
  )
}

export function useImporterViewModel(): ImporterContextType {
  const context = useContext(ImporterContext)
  if (!context) {
    throw new Error('useImporterViewModel must be used within ImporterProvider')
  }
  return context
}
// === End Context Setup ===

export function toRowsJSON(data: ImportData): ImportDataRowsFormat {
  if (!data.rows.length)
    return {
      headers: [],
      rows: {
        columns: [],
        rows: [],
      },
    }
  const columns = Object.keys(data.rows[0])
  const rows = data.rows.map((obj) => columns.map((key) => obj[key]))
  return {
    headers: data.headers,
    rows: {
      columns,
      rows,
    },
  }
}

/**
 * Inverse of toRowsJSON — reconstructs ImportData from the wire format.
 * Each row array is zipped back into an object keyed by data.rows.columns;
 * headers are carried through unchanged.
 */
export function fromRowsJSON(data: ImportDataRowsFormat): ImportData {
  const { columns, rows } = data.rows
  const csvRows: CSVRow[] = rows.map((rowArray) => {
    const obj: Record<string, any> = {}
    columns.forEach((col, i) => {
      obj[col] = rowArray[i]
    })
    return obj as CSVRow
  })
  return {
    headers: data.headers,
    rows: csvRows,
  }
}

// columnTypeColors, columnOptions, ColumnOptionKey, and getColumnOption have been
// moved to ./columnMappingShared. Re-exported here so existing importers keep working.
export {
  columnTypeColors,
  columnOptions,
  getColumnOption,
} from './columnMappingShared'
export type { ColumnOptionKey } from './columnMappingShared'

const chainSuggestionKeys = [
  'name',
  'domain',
  'count',
  'item_id',
  'type',
  'score',
] as Array<keyof ChainSearchResult>

const generatedColumnKeys = [
  '__generated__firstbite_chain_id',
  '__generated__chain_name',
  '__generated__chain_domain',
  '__generated__chain_size',
]
